"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import toast from "react-hot-toast";

const initialState = {
  fullName: "",
  phone: "",
  email: "",
  course: "",
};

const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
const validatePhone = (value) => /^[6-9]\d{9}$/.test(String(value || "").replace(/\D/g, ""));

export default function HeroLeadForm() {
  const router = useRouter();
  const [formData, setFormData] = useState(initialState);
  const [courses, setCourses] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const loadCourses = async () => {
      try {
        const res = await fetch(`${process.env.NEXT_PUBLIC_API_BASE_URL}/courses/course/getall`, {
          cache: "no-store",
        });
        if (!res.ok) return;
        const data = await res.json();
        if (Array.isArray(data)) setCourses(data);
      } catch {
        // ignore
      }
    };

    loadCourses();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!formData.fullName.trim()) nextErrors.fullName = "Name is required.";
    if (!validatePhone(formData.phone)) nextErrors.phone = "Valid phone is required.";
    if (!validateEmail(formData.email)) nextErrors.email = "Valid email is required.";
    if (!formData.course.trim()) nextErrors.course = "Select a course.";
    return nextErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const selectedCourse = courses.find(
        (course) => String(course?.id || course?._id || "") === String(formData.course)
      );
      const res = await fetch(`${process.env.NEXT_PUBLIC_API_BASE_URL}/enquiry/contact-us`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          data: {
            name: `${formData.fullName} (${selectedCourse?.title || "Demo Enquiry"})`,
            number: formData.phone,
            email: formData.email,
          },
        }),
      });

      if (!res.ok) throw new Error("Failed to submit lead");
      toast.success("Taking you to demo signup.");

      const params = new URLSearchParams({
        name: formData.fullName,
        email: formData.email,
        phone_number: formData.phone,
      });
      if (selectedCourse?.id || selectedCourse?._id) {
        params.set("course_id", String(selectedCourse?.id || selectedCourse?._id));
      }
      if (selectedCourse?.title) {
        params.set("course_title", selectedCourse.title);
      }

      router.push(`/register?${params.toString()}`);
    } catch {
      toast.error("Could not submit right now.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-[420px] rounded-[28px] border border-white/15 bg-white p-5 text-[#1a1a1a] shadow-[0_20px_60px_rgba(0,0,0,0.18)]">
      <p className="text-xs uppercase tracking-[0.28em] text-primary">
        Book Free Demo
      </p>
      <h3 className="mt-3 text-2xl font-semibold leading-tight">
        Start with a quick enquiry
      </h3>
      <form onSubmit={handleSubmit} className="mt-5 flex flex-col gap-3">
        <input
          name="fullName"
          value={formData.fullName}
          onChange={handleChange}
          placeholder="Full Name"
          className="rounded-2xl border border-[#d9e6f8] bg-[#f8fbff] px-4 py-3 text-[#1a1a1a] outline-none"
        />
        {errors.fullName ? <p className="text-xs text-red-500">{errors.fullName}</p> : null}
        <input
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          placeholder="Phone Number"
          className="rounded-2xl border border-[#d9e6f8] bg-[#f8fbff] px-4 py-3 text-[#1a1a1a] outline-none"
        />
        {errors.phone ? <p className="text-xs text-red-500">{errors.phone}</p> : null}
        <input
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="Email Address"
          className="rounded-2xl border border-[#d9e6f8] bg-[#f8fbff] px-4 py-3 text-[#1a1a1a] outline-none"
        />
        {errors.email ? <p className="text-xs text-red-500">{errors.email}</p> : null}
        <select
          name="course"
          value={formData.course}
          onChange={handleChange}
          className="rounded-2xl border border-[#d9e6f8] bg-[#f8fbff] px-4 py-3 text-[#1a1a1a] outline-none"
        >
          <option value="">Course Interested In</option>
          {courses.map((course) => (
            <option key={course?.id || course?._id} value={course?.id || course?._id || ""}>
              {course?.title}
            </option>
          ))}
        </select>
        {errors.course ? <p className="text-xs text-red-500">{errors.course}</p> : null}
        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded-2xl bg-[#ffc027] px-4 py-3 font-semibold text-[#0a2f5e] disabled:opacity-60"
        >
          {isSubmitting ? "Submitting..." : "Book Free Demo Class"}
        </button>
      </form>
    </div>
  );
}
